/**
 * Meta anual — numerador vindo do servidor, com o valor client-side como piso.
 *
 * ## O contrato
 *
 * `count()` é exato mas **assíncrono e não reativo** (agregação não tem
 * `onSnapshot`). Duas consequências que este hook resolve para os 5 call sites,
 * em vez de deixar cada tela improvisar:
 *
 * 1. **Enquanto a contagem não chega, mostra o `fallback`** — a contagem
 *    client-side sobre a janela carregada. Ela pode subcontar, mas é o número
 *    que a tela já mostrava antes desta mudança; nunca é *pior* que o estado
 *    anterior, e não pisca um `0` no lugar da meta.
 * 2. **Se a busca falhar** (offline, índice ausente), o `fallback` fica. Uma
 *    meta desatualizada é melhor que uma meta zerada — e o app funciona
 *    offline por desenho (`persistentLocalCache`).
 *
 * Depois que um valor do servidor chega, ele **não volta** para o fallback numa
 * recontagem: a tela segue mostrando o último número exato enquanto o próximo
 * está em voo. Sem isso, toda escrita na estante faria a meta oscilar.
 *
 * ## Quando recontar
 *
 * Agregação não observa mudança, então quem chama passa `revision` — uma
 * assinatura barata da estante. Use `shelfRevision()`: muda quando um livro
 * entra, sai ou é tocado, e não muda quando o React só re-renderiza.
 */

import type { BookStatus, UserBook } from "../../../services/firebase/booksService";
import {
  countBooksFinishedInYearServer,
  countShelfByStatus,
} from "../../../services/firebase/shelfCounts";
import { toMs } from "../../../utils/timestampUtils";
import { useServerValue } from "./useServerValue";

/**
 * Assinatura da estante para disparar recontagem.
 *
 * `length` pega entrada e saída de livro; o `updatedAt` máximo pega mudança de
 * status e de data de conclusão — que é o que move a meta sem mexer no total.
 * As duas juntas cobrem toda escrita que o trigger propaga.
 */
export function shelfRevision(books: UserBook[]): string {
  let maxUpdated = 0;
  for (const book of books) {
    const ms = toMs(book.updatedAt);
    if (ms > maxUpdated) maxUpdated = ms;
  }
  return `${books.length}:${maxUpdated}`;
}

export function useBooksFinishedInYear(args: {
  userId: string | null | undefined;
  year: number;
  /** Contagem sobre a janela carregada — piso enquanto o servidor não responde. */
  fallback: number;
  /** Assinatura da estante; mudou, reconta. Ver `shelfRevision`. */
  revision?: string | number;
}): number {
  const { userId, year, fallback, revision } = args;
  // A etiqueta carrega o ANO além do usuário: a meta de 2025 e a de 2026 são
  // perguntas diferentes sobre a mesma pessoa.
  return useServerValue({
    key: userId ? `${userId}:${year}` : null,
    fallback,
    revision,
    fetch: () => countBooksFinishedInYearServer(userId!, year),
  });
}

/**
 * As quatro contagens da estante, exatas — as abas do seletor e os tiles do
 * Perfil.
 *
 * Mesmo contrato da meta (ver `useServerValue`): o `fallback` é a derivação
 * client-side sobre a estante carregada, que é o que a tela mostrava antes.
 */
export function useShelfCounts(args: {
  userId: string | null | undefined;
  fallback: Record<BookStatus, number>;
  revision?: string | number;
}): Record<BookStatus, number> {
  const { userId, fallback, revision } = args;
  return useServerValue({
    key: userId ?? null,
    fallback,
    revision,
    fetch: () => countShelfByStatus(userId!),
  });
}
